import React from 'react';
import { useTranslation } from 'react-i18next';

const Eprto = () => {
  const { t } = useTranslation('help');

  return (
    <div style={styles.container}>
      {/* Main Title */}
      <h2 style={styles.title}>
        {t('eprto.title', 'Response Time Optimization (RTO)')}
      </h2>

      {/* Section (a) - Purpose */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(a)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('eprto.labels.purpose', 'Purpose:-')}
            </strong>{' '}
            {t(
              'eprto.descriptions.purpose',
              'This target for AI prompts focuses on reducing latency—the time it takes for a model to process a prompt and generate a response'
            )}
          </p>
        </div>
      </div>

      {/* Section (b) - Endpoint */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(b)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('eprto.labels.endpoint', 'Target :-')}
            </strong>{' '}
            <a 
              href="http://172.31.30.217:8002/chat" 
              style={styles.link} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              http://172.31.30.217:8002/chat
            </a>
          </p>
          <p style={styles.paragraph}>
            {t(
              'eprto.descriptions.endpoint',
              'The simulator accepts an application/json body. AUTH_KEY (Provided By WhiteHaX) is required inside request_template and must match an authorized key. The target validates it on every request and returns HTTP 401 when it is missing or invalid.'
            )}
          </p>
        </div>
      </div>

      {/* Section (c) - Request Json */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(c)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('eprto.labels.requestJson', 'Request Json')}
            </strong>
          </p>

          {/* Request JSON Code Block */}
          <div style={styles.codeBlock}>
            <div>{'{'}</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"headers"</span>: {'{'}
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"Content-Type"</span>: <span style={styles.jsonString}>"application/json"</span>
            </div>
            <div style={styles.indent1}>{'}'},</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"data_type"</span>: <span style={styles.jsonString}>"json"</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"request_template"</span>: {'{'}
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"AUTH_KEY"</span>: <span style={styles.jsonString}>"&lt;authorized-key&gt;"</span>,
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"prompt"</span>: <span style={styles.jsonString}>"{"{{PROMPT}}"}"</span>
            </div>
            <div style={styles.indent1}>{'}'},</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"prompt_path"</span>: <span style={styles.jsonString}>"prompt"</span>
            </div>
            <div>{'}'}</div>
          </div>
        </div>
      </div>

      {/* Section (d) - Response Json */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(d)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('eprto.labels.responseJson', 'Response Json')}
            </strong>
          </p>
          <p style={styles.paragraph}>
            {t(
              'eprto.descriptions.responseJson',
              'The response field contains the generated text. The target also reports the detected prompt size, generated page count, word count, and simulated processing time.'
            )}
          </p>

          {/* Response JSON Code Block */}
          <div style={styles.codeBlock}>
            <div>{'{'}</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"detected_prompt_size"</span>: <span style={styles.jsonString}>"regular"</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"pages_generated"</span>: <span style={styles.jsonNumber}>1</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"word_count"</span>: <span style={styles.jsonNumber}>400</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"simulated_processing_time_sec"</span>: <span style={styles.jsonNumber}>0.42</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"response"</span>: <span style={styles.jsonString}>"{"{{RESP}}"}"</span>
            </div>
            <div>{'}'}</div>
          </div>
        </div>
      </div>

      {/* Section (e) - Expected Test Results */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(e)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('eprto.labels.expectedResults', 'Expected Test Results against this simulator:-')}
            </strong>{' '}
            {t(
              'eprto.descriptions.expectedResults',
              'The expected test result should provide a valid response along with prompt size, page count, word count, and simulated processing time so that response-time performance can be assessed.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

// Component Styles (Dark Theme)
const styles = {
  container: {
    maxWidth: '850px',
    margin: '0 auto',
    padding: '24px',
    color: '#e0e0e0',
    backgroundColor: '#181a1f',
    borderRadius: '8px',
    fontFamily: 'Segoe UI, Roboto, sans-serif'
  },
  title: {
    margin: '0 0 20px 0',
    fontWeight: 'bold',
    color: '#ffffff',
    fontSize: '20px'
  },
  section: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '15px'
  },
  sectionLabel: {
    width: '32px',
    flexShrink: 0,
    fontWeight: 'bold',
    color: '#a0a0a0'
  },
  sectionContent: {
    flexGrow: 1
  },
  paragraph: {
    margin: '0 0 10px 0',
    lineHeight: '1.6',
    color: '#cccccc',
    fontSize: '14px'
  },
  strongText: {
    color: '#ffffff'
  },
  link: {
    color: '#4fa3d1',
    textDecoration: 'underline'
  },
  codeBlock: {
    backgroundColor: '#0d0e11',
    color: '#e0e0e0',
    padding: '16px 20px',
    margin: '12px 0',
    borderRadius: '6px',
    border: '1px solid #2a2d34',
    fontFamily: 'Consolas, Monaco, "Andale Mono", monospace',
    fontSize: '13px',
    lineHeight: '1.5',
    overflowX: 'auto',
    whiteSpace: 'pre'
  },
  indent1: {
    paddingLeft: '20px'
  },
  indent2: {
    paddingLeft: '40px'
  },
  jsonKey: {
    color: '#6fc2ef'
  },
  jsonString: {
    color: '#e59373'
  },
  jsonNumber: {
    color: '#b5cea8'
  }
};

export default Eprto;